import * as React from 'react';
import { GenderEnum } from 'types/generated/client';
import {
  ALL_TENNIS_SYSTEM_IDS,
  PRIMARY_TENNIS_SYSTEM_IDS,
  TENNIS_SYSTEMS,
  TennisRatingSystem,
  convertTennisRating,
  formatTennisRatingRange,
  formatTennisRatingValue,
} from 'utils/shared/tennisRatingConverter';
import { trackEvent } from 'services/client/analytics';
import { TOOL_RATING_CONVERTER_USED } from 'services/client/analytics/events';

interface Props {
  initialFromSystem?: TennisRatingSystem;
  initialValue?: number;
  onResultChange?: (
    fromSystem: TennisRatingSystem,
    value: number,
    gender: GenderEnum | null,
  ) => void;
}

const DEFAULT_VALUES: Record<TennisRatingSystem, number> = {
  utr: 6.0,
  ntrp: 3.5,
  dynamic_ntrp: 3.5,
  wtn: 17.0,
  itn: 7.0,
  self_rated: 3.5,
};

const GENDER_OPTIONS: Array<{ value: GenderEnum | null; label: string }> = [
  { value: GenderEnum.Male, label: 'Men’s' },
  { value: GenderEnum.Female, label: 'Women’s' },
  { value: null, label: 'Mixed / Skip' },
];

export default function Converter({
  initialFromSystem = 'utr',
  initialValue,
  onResultChange,
}: Props) {
  const [fromSystem, setFromSystem] = React.useState<TennisRatingSystem>(initialFromSystem);
  const [value, setValue] = React.useState<number>(
    initialValue ?? DEFAULT_VALUES[initialFromSystem],
  );
  const [rawInput, setRawInput] = React.useState<string>(String(value));
  const [gender, setGender] = React.useState<GenderEnum | null>(GenderEnum.Male);

  const fromDef = TENNIS_SYSTEMS[fromSystem];
  const isNtrpFamily =
    fromSystem === 'ntrp' || fromSystem === 'dynamic_ntrp' || fromSystem === 'self_rated';

  const conversions = React.useMemo(
    () => convertTennisRating(value, fromSystem, { gender }),
    [value, fromSystem, gender],
  );

  React.useEffect(() => {
    onResultChange?.(fromSystem, value, gender);
  }, [fromSystem, value, gender, onResultChange]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      trackEvent(TOOL_RATING_CONVERTER_USED, {
        sport: 'tennis',
        from_system: fromSystem,
        from_value: value,
        gender,
      });
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [fromSystem, value, gender]);

  const handleSystemChange = (next: TennisRatingSystem) => {
    setFromSystem(next);
    const nextDefault = DEFAULT_VALUES[next];
    setValue(nextDefault);
    setRawInput(String(nextDefault));
  };

  const handleValueChange = (raw: string) => {
    setRawInput(raw);
    const parsed = parseFloat(raw);
    if (!Number.isNaN(parsed)) {
      setValue(parsed);
    }
  };

  return (
    <div className="border-y border-color-divider-lightmode-primary py-8 dark:border-color-divider-darkmode-primary md:py-10">
      <div className="relative overflow-hidden rounded-3xl border border-color-divider-lightmode-primary bg-color-bg-lightmode-secondary/50 p-6 dark:border-color-divider-darkmode-primary dark:bg-color-bg-darkmode-secondary/20 md:p-10">
        <div className="flex flex-col items-center gap-8 md:flex-row md:items-center md:justify-between">
          <label className="flex w-full flex-col items-center">
            <span className="typography-product-subheading flex items-center gap-2 text-xs uppercase tracking-widest text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Enter Your Rating
            </span>
            <input
              type="number"
              inputMode="decimal"
              min={fromDef.scaleMin}
              max={fromDef.scaleMax}
              step={fromDef.step}
              value={rawInput}
              onChange={(e) => handleValueChange(e.target.value)}
              className="mt-2 w-full max-w-[240px] border-b-4 border-dashed border-color-divider-lightmode-primary bg-transparent pb-2 text-center font-mono text-7xl font-black tracking-tighter text-color-text-lightmode-primary outline-none transition-all hover:border-color-text-lightmode-tertiary focus:border-solid focus:border-color-bg-lightmode-brand focus:opacity-100 dark:border-color-divider-darkmode-primary dark:text-color-text-darkmode-primary dark:hover:border-color-text-darkmode-tertiary md:text-8xl [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              aria-label="Rating value"
            />
            <span className="mt-3 text-sm font-medium text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {fromDef.shortName} scale: {fromDef.scaleMin.toFixed(1)}–{fromDef.scaleMax.toFixed(1)}
              {fromDef.isInverted ? ' (lower = stronger)' : ''}
            </span>
          </label>

          <div className="flex w-full flex-col gap-3 md:w-auto md:max-w-md">
            <span className="typography-product-subheading text-center text-xs uppercase tracking-widest text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary md:text-left">
              From System
            </span>
            <div className="flex flex-wrap justify-center gap-2 md:justify-start">
              {ALL_TENNIS_SYSTEM_IDS.map((id) => {
                const isSelected = fromSystem === id;
                return (
                  <button
                    key={id}
                    onClick={() => handleSystemChange(id)}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      isSelected
                        ? 'bg-color-bg-lightmode-brand text-white shadow-md'
                        : 'border border-color-divider-lightmode-primary bg-color-bg-lightmode-primary text-color-text-lightmode-secondary hover:bg-color-bg-lightmode-secondary dark:border-color-divider-darkmode-primary dark:bg-color-bg-darkmode-primary dark:text-color-text-darkmode-secondary dark:hover:bg-color-bg-darkmode-secondary'
                    }`}
                  >
                    {TENNIS_SYSTEMS[id].shortName}
                    {TENNIS_SYSTEMS[id].isLegacy ? '*' : ''}
                  </button>
                );
              })}
            </div>
            <p className="typography-product-body mt-2 text-center text-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary md:text-left">
              {fromDef.oneLineDescription}
            </p>

            {isNtrpFamily && (
              <div className="mt-2 flex flex-col gap-2">
                <span className="typography-product-subheading text-center text-xs uppercase tracking-widest text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary md:text-left">
                  Division (for NTRP → UTR accuracy)
                </span>
                <div className="flex flex-wrap justify-center gap-2 md:justify-start">
                  {GENDER_OPTIONS.map((opt) => {
                    const isSelected = gender === opt.value;
                    return (
                      <button
                        key={opt.label}
                        onClick={() => setGender(opt.value)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                          isSelected
                            ? 'bg-color-bg-lightmode-invert text-color-text-lightmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert'
                            : 'border border-color-divider-lightmode-primary bg-color-bg-lightmode-primary text-color-text-lightmode-secondary hover:bg-color-bg-lightmode-secondary dark:border-color-divider-darkmode-primary dark:bg-color-bg-darkmode-primary dark:text-color-text-darkmode-secondary dark:hover:bg-color-bg-darkmode-secondary'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-center text-xs text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary md:text-left">
                  Women’s NTRP maps about half a level lower in UTR than men’s NTRP at the same number.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="typography-product-subheading mb-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          Equivalents in other systems
        </h3>
        <div className="grid grid-cols-1 border-t border-l border-color-divider-lightmode-primary dark:border-color-divider-darkmode-primary sm:grid-cols-2 lg:grid-cols-3">
          {conversions
            .filter((c) => PRIMARY_TENNIS_SYSTEM_IDS.includes(c.system))
            .sort((a, b) => {
              if (a.system === fromSystem) return -1;
              if (b.system === fromSystem) return 1;
              return (
                PRIMARY_TENNIS_SYSTEM_IDS.indexOf(a.system) -
                PRIMARY_TENNIS_SYSTEM_IDS.indexOf(b.system)
              );
            })
            .map((c) => {
              const def = TENNIS_SYSTEMS[c.system];
              const isSelected = c.system === fromSystem;
              return (
                <article
                  key={c.system}
                  className={`flex flex-col gap-1 border-b border-r border-color-divider-lightmode-primary p-4 dark:border-color-divider-darkmode-primary ${
                    isSelected
                      ? 'bg-color-bg-lightmode-secondary/50 dark:bg-color-bg-darkmode-secondary/50'
                      : ''
                  }`}
                  aria-label={`${def.shortName} equivalent`}
                >
                  <header className="flex items-baseline justify-between gap-2">
                    <span className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      {def.shortName}
                      {isSelected && (
                        <span className="ml-2 text-xs font-normal text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                          (Your rating)
                        </span>
                      )}
                    </span>
                    <span className="typography-product-heading text-color-bg-lightmode-brand">
                      {formatTennisRatingValue(c.value, c.system)}
                    </span>
                  </header>
                  <p className="typography-product-body text-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    Range: {formatTennisRatingRange(c.rangeLow, c.rangeHigh, c.system)}
                    {def.isInverted ? ' (lower = stronger)' : ''}
                  </p>
                  <p className="typography-product-body text-sm text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                    {def.oneLineDescription}
                  </p>
                </article>
              );
            })}
        </div>
        <p className="typography-product-body text-sm mt-4 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
          Conversions are approximate since no organization publishes an official cross-system
          table. Ranges reflect the typical spread we’d expect for the same player.
        </p>
      </div>
    </div>
  );
}
